import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  BehaviorSubject,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IUser } from '../_models/interfaces/User';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private jwtHelper: JwtHelperService = new JwtHelperService();
  private refreshInProgress = false;
  private refreshTokenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public accessTokenKey = 'access_tk_urb_red';
  public refreshTokenKey = 'refresh_tk_urb_red';

  public user!: IUser | null;

  public isLogged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  constructor(private router: Router, private http: HttpClient) {}

  initAuth(): Observable<boolean> {
    return this.getAuthentication$().pipe(
      map((x) => {
        this.isLogged$.next(x);
        return x;
      }),
      catchError(() => {
        this.isLogged$.next(false);
        return of(false);
      })
    );
  }

  public get tokenJWT(): string {
    return localStorage.getItem(this.accessTokenKey) || '';
  }

  public set tokenJWT(val) {
    localStorage.setItem(this.accessTokenKey, val);
  }

  public getAuthentication$(shouldRefresh = true): Observable<boolean> {
    const token = localStorage.getItem(this.accessTokenKey);
    // Check whether the token is expired and return true or false
    const auth = !!token && !this.jwtHelper.isTokenExpired(token);

    if (!auth && shouldRefresh && localStorage.getItem(this.refreshTokenKey)) {
      return this.refreshToken().pipe(
        switchMap(() => this.getAuthentication$(false))
      );
    }
    if (auth) {
      this.getUser();
    }

    return of(auth);
  }

  public getUser(): IUser {
    const token = localStorage.getItem(this.accessTokenKey);
    const decoded = this.jwtHelper.decodeToken(token || '');

    const user = {
      id: +decoded?.userId,
      roles: decoded?.roles,
      firstname: decoded?.firstname,
      lastname: decoded?.lastname,
      email: decoded?.email,
      tenantId: +decoded?.tenantId,
      claims: decoded?.claims,
    } as IUser;

    console.log(user);
    return user;
  }

  login(
    username: string,
    password: string
  ): Observable<{ access_token: string; refresh_token: string }> {
    const url = `${environment.authUrl}/token`;
    const params = new URLSearchParams();

    params.append('username', username);
    params.append('password', password);
    params.append('grant_type', 'password');

    return this.http
      .post<{ access_token: string; refresh_token: string }>(url, params, {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(
        map((response) => {
          localStorage.setItem(this.accessTokenKey, response.access_token);
          localStorage.setItem(this.refreshTokenKey, response.refresh_token);

          this.isLogged$.next(true);
          this.router.navigateByUrl('pictures');
          // Redirect the user to the requested page if it exists, or to the home page otherwise
          // const redirectUrl = this.router.parseUrl(this.router.url).queryParams['redirectUrl'] || '/';
          // this.router.navigateByUrl(redirectUrl);
          this.getUser();

          return response;
        })
      );
  }

  public logout(): void {
    // Remove the token from localStorage
    this.isLogged$.next(false);
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    this.refreshInProgress = false;
    this.refreshTokenSubject.next(false);
    this.router.navigateByUrl('');
    // // Redirect the user to the login page
    this.user = null;
  }

  public getRedirectUrl(): string {
    // Return the requested URL before the user was redirected to the login page
    return (
      this.router.parseUrl(this.router.url).queryParams['redirectUrl'] || '/'
    );
  }

  public refreshToken(): Observable<{
    access_token: string;
    refresh_token: string;
  }> {
    if (!this.refreshInProgress) {
      this.refreshInProgress = true;
      const refreshToken = localStorage.getItem(this.refreshTokenKey);
      const params = new URLSearchParams();

      params.append('refresh_token', refreshToken || '');
      params.append('grant_type', 'refresh_token');

      return this.http
        .post<{ access_token: string; refresh_token: string }>(
          `${environment.authUrl}/token`,
          params,
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/x-www-form-urlencoded',
            }),
          }
        )
        .pipe(
          tap(({ access_token, refresh_token }) => {
            localStorage.setItem(this.accessTokenKey, access_token);
            localStorage.setItem(this.refreshTokenKey, refresh_token);
            this.refreshInProgress = false;
            this.refreshTokenSubject.next(true);
          }),
          catchError((error) => {
            if ([400, 401].includes(error.status)) {
              localStorage.removeItem(this.accessTokenKey);
              localStorage.removeItem(this.refreshTokenKey);
            }
            this.refreshInProgress = false;
            this.refreshTokenSubject.next(false);
            return throwError(error);
          })
        );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((refreshing) => !refreshing),
        take(1),
        switchMap(() => this.refreshToken())
      );
    }
  }
}
